import React, { useEffect, useState } from 'react';
import {
  Box, Container, Grid, useMediaQuery, useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import Head from 'next/head';
import axios, { API } from 'lib/config/axios';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import generateMetaData from 'lib/utils/generateMetaData';
import { MAP_CENTER } from 'views/rebranded-module/location-module/LocationsViewV2/utils/constants';
import { PAGES_GA_IDENTIFIERS } from 'views/common/utils/constants';
import StyledBox, {
  StyledLocationsMapWrapperV2,
} from 'views/rebranded-module/location-module/LocationsViewV2/LocationsViewV2.styles';
import omit from 'lib/utils/omit';
import {
  FloatingButtonWithFloatingDialogV2,
  Typography700,
} from 'views/rebranded-module/common/components';
import {
  MapPlaceHolderV2,
  LocationsWrapperV2,
} from 'views/rebranded-module/location-module/LocationsViewV2/components';
import { LazyLoad } from 'views/common/components';
import { LocationsSkeletonV2 } from 'views/rebranded-module/location-module/LocationsViewV2';
import { useLocationsCMSData } from 'views/rebranded-module/location-module/LocationsViewV2/hooks/useLocationsCMSData';
import { useContent } from 'views/rebranded-module/common/context/useContent';

const LocationsViewV2 = (props) => {
  const { serverData } = props;
  const isCrawler = useSelector((state) => state.globalData.isCrawler);
  const cmsContent = useContent();
  const { data: i18nContent } = useLocationsCMSData();
  const content = cmsContent || i18nContent;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const handleErrorResponse = useErrorsHandler();
  const appNavigator = useAppNavigator();
  const [loadMap, setLoadMap] = useState(!isCrawler);

  const initLocationsData = (data) => {
    if (data) {
      return {
        usLocations: data.us_locations,
        nonUsLocations: data.non_us_locations,
        usCountry: data.us_country,
        defaultVehicleType: data.default_vehicle_type,
      };
    }
    return {};
  };
  const [locationsData, setLocationsData] = useState(
    initLocationsData(serverData),
  );
  const [mapInfo, setMapInfo] = useState(serverData?.map_info || []);
  const [seoValues, setSeoValues] = useState(serverData?.seo_values || {});
  const [mapCenter, setMapCenter] = useState(
    // eslint-disable-next-line no-nested-ternary
    isMobile
      ? MAP_CENTER.US
      : appNavigator.query.club_locations_segment
        ? MAP_CENTER.CLUB
        : MAP_CENTER.ALL_LOCATIONS,
  );
  const [loaded, setLoaded] = useState(!!serverData);
  const [widgetDialogData, setWidgetDialogData] = useState({});

  const getLocationsData = (onlyClub) => axios
    .get(API.locations, {
      params: {
        only_club_ones: onlyClub,
        preferred_seo_identifer: onlyClub
          ? 'club-locations-page'
          : 'locations-page',
      },
    })
    .then((res) => {
      setLocationsData({
        usLocations: res.data.us_locations,
        nonUsLocations: res.data.non_us_locations,
        usCountry: res.data.us_country,
        defaultVehicleType: res.data.default_vehicle_type,
      });

      const mapInformations = onlyClub
        ? res.data.map_info.filter((loc) => loc.participating_in_membership)
        : res.data.map_info;

      setMapInfo(
        mapInformations.map((item) => ({
          ...item,
          show: false,
        })),
      );

      setSeoValues(res.data.seo_values);
      setLoaded(true);
    })
    .catch((error) => {
      handleErrorResponse(error, false);
    });
  const handleShowMapClick = () => setLoadMap(true);

  const handleFloatingPointClick = () => {
    setWidgetDialogData({ open: true });
  };
  const handleWidgetDialogClose = () => {
    setWidgetDialogData({ open: false });
  };
  const handleBookNowClick = (location) => {
    setWidgetDialogData({
      open: true,
      pickup_location_id: location.id,
      dropoff_location_id: location.id,
    });
  };
  useEffect(() => {
    window.gtag('event', 'landing_page_view', {
      page_identifier: PAGES_GA_IDENTIFIERS.LOCATIONS,
    });
  }, []);

  return (
    <StyledBox>
      <>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1"
          />
          {generateMetaData(seoValues)}
        </Head>

        <Box id="mapContainer">
          <Container maxWidth="xxl" className="LVContainer">
            <Grid container className="LVHeader">
              <Grid item>
                <Typography700 variant="h2" component="h1">
                  {content.LocationsPage.pageTitle}
                </Typography700>
              </Grid>
              <Grid item>
                <FloatingButtonWithFloatingDialogV2
                  text={content.LocationsPage.makeReservationBtn}
                  widgetDialogData={widgetDialogData}
                  reservationData={omit(widgetDialogData, ['open'])}
                  handleWidgetDialogClose={handleWidgetDialogClose}
                  handleFloatingPointClick={handleFloatingPointClick}
                />
              </Grid>
            </Grid>
          </Container>
          {loadMap ? (
            <StyledLocationsMapWrapperV2
              mapInfo={mapInfo}
              setMapInfo={setMapInfo}
              mapCenter={mapCenter}
              setMapCenter={setMapCenter}
              handleBookNowClick={handleBookNowClick}
              content={content.FloatingLocationInfoComponent}
            />
          ) : (
            <MapPlaceHolderV2
              onClick={handleShowMapClick}
              content={content.FloatingLocationInfoComponent}
            />
          )}
        </Box>
        <LazyLoad skeleton={<LocationsSkeletonV2 />} dataLoaded={loaded}>
          <LocationsWrapperV2
            usCountry={locationsData.usCountry}
            usLocations={locationsData.usLocations}
            nonUsLocations={locationsData.nonUsLocations}
            setMapCenter={setMapCenter}
            defaultVehicleType={locationsData.defaultVehicleType}
            getLocationsData={getLocationsData}
            content={content}
          />
        </LazyLoad>
      </>
    </StyledBox>
  );
};

export { LocationsViewV2 };
